import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { Tabs, XYScatterPlot } from "@jana-technology/platform-ui";

import {
  RiskManagementMajorEventComparisonChartDocument,
  RiskManagementOverallComparisonChartDocument,
} from "$/__generated__/graphql";
import { useGlobalFiltersContext } from "contexts/filters/global-filters";
import { useRiskManagementFiltersContext } from "contexts/filters/risk-management-filters";
import { useBaseBarChartProps } from "hooks/useBaseBarChartProps";

import ChartCard from "./ChartCard";
import type {
  RiskComparisonCardHeadingProps,
  RiskComparisonChartConfig,
} from "./types";
import {
  buildMitigationRiskComparisonChartConfig,
  buildRiskComparisonChartConfig,
} from "./helpers";

export const RiskComparisonCardHeading = ({
  onValueChange,
}: RiskComparisonCardHeadingProps) => (
  <div className="flex items-baseline justify-between">
    <span>Risk Comparison</span>
    <Tabs
      kind="secondary"
      className="font-normal"
      items={[
        {
          label: "Operational Risk",
          id: "risk-comparison-or",
        },
        {
          label: "Intervention Risk",
          id: "risk-comparison-ir",
        },
      ]}
      onValueChange={onValueChange}
    />
  </div>
);

export const OverallRiskComparison = () => {
  const {
    appliedWell,
    filteredWellNamesByHierarchyUnfiltered,
    assetSelectionQueryResult,
  } = useGlobalFiltersContext();
  const { appliedRiskMitigationId } = useRiskManagementFiltersContext();

  const [hierarchyId, wellName] = appliedWell;

  const [isBaseline, setIsBaseline] = useState(true);

  const onValueChange = useCallback((value: string) => {
    if (value === "risk-comparison-or") {
      setIsBaseline(true);
    } else {
      setIsBaseline(false);
    }
  }, []);

  const overallRiskComparisonResult = useQuery(
    RiskManagementOverallComparisonChartDocument,
    {
      skip: !wellName.length,
      variables: {
        hierarchyId,
        wellName,
        isBaseline,
        riskMitigationTypeId: appliedRiskMitigationId,
      },
    },
  );

  const { height, yTickFormatter } = useBaseBarChartProps();

  const { series, referenceLines, legendDomain, theme, legendColors } =
    useMemo<RiskComparisonChartConfig>(() => {
      return buildRiskComparisonChartConfig(
        {
          chartData:
            overallRiskComparisonResult.data
              ?.riskManagementOverallComparisonChart,
          wellName,
          hierarchyId,
          wellNamesByHierarchy: filteredWellNamesByHierarchyUnfiltered,
          hierarchies: assetSelectionQueryResult.data?.hierarchies,
        },
        isBaseline,
        true,
        yTickFormatter,
      );
    }, [
      hierarchyId,
      overallRiskComparisonResult.data,
      wellName,
      filteredWellNamesByHierarchyUnfiltered,
      isBaseline,
      assetSelectionQueryResult.data?.hierarchies,
      yTickFormatter,
    ]);

  // this chart doesn't read yAxisLabel from a route handle; isBaseline maps to OR|IR
  return (
    <ChartCard
      heading={<RiskComparisonCardHeading onValueChange={onValueChange} />}
      loading={overallRiskComparisonResult.loading}
    >
      <XYScatterPlot
        height={height}
        series={series}
        xAxisLabel="Nth-Ranked Well"
        yAxisLabel={`${isBaseline ? "Operational Risk" : "Intervention Risk"}`}
        theme={theme}
        referenceLines={referenceLines}
        legendDomain={legendDomain}
        legendRange={legendColors}
        yTickFormatter={yTickFormatter}
        yDomainPadding={1.05}
        yScale={{
          type: "log",
        }}
      />
    </ChartCard>
  );
};

export const MitigationRiskComparison = () => {
  const {
    appliedWell,
    filteredWellNamesByHierarchyUnfiltered,
    assetSelectionQueryResult,
    riskMitigationQueryResult,
  } = useGlobalFiltersContext();
  const { appliedRiskMitigationId } = useRiskManagementFiltersContext();

  const [hierarchyId, wellName] = appliedWell;

  const { riskMitigations } = riskMitigationQueryResult.data ?? {};

  const riskMitigationName =
    riskMitigations?.find(
      (mitigation) => mitigation?.id === appliedRiskMitigationId,
    )?.value ?? "";

  const overallRiskComparisonResult = useQuery(
    RiskManagementOverallComparisonChartDocument,
    {
      skip: !wellName.length,
      variables: {
        hierarchyId,
        wellName,
        isBaseline: true,
        riskMitigationTypeId: appliedRiskMitigationId,
      },
    },
  );

  const { height, yTickFormatter } = useBaseBarChartProps();

  const { series, referenceLines, legendDomain, theme, legendColors } =
    useMemo<RiskComparisonChartConfig>(() => {
      return buildMitigationRiskComparisonChartConfig(
        {
          chartData:
            overallRiskComparisonResult.data
              ?.riskManagementOverallComparisonChart,
          wellName,
          hierarchyId,
          wellNamesByHierarchy: filteredWellNamesByHierarchyUnfiltered,
          hierarchies: assetSelectionQueryResult.data?.hierarchies,
        },
        riskMitigationName,
        yTickFormatter,
      );
    }, [
      hierarchyId,
      overallRiskComparisonResult.data,
      wellName,
      filteredWellNamesByHierarchyUnfiltered,
      assetSelectionQueryResult.data?.hierarchies,
      riskMitigationName,
      yTickFormatter,
    ]);

  // this chart doesn't read yAxisLabel from a route handle; isBaseline maps to OR|IR
  return (
    <ChartCard
      heading="Risk Comparison"
      loading={overallRiskComparisonResult.loading}
    >
      <XYScatterPlot
        height={height}
        series={series}
        xAxisLabel="Nth-Ranked Well"
        yAxisLabel="Operational Risk"
        theme={theme}
        referenceLines={referenceLines}
        legendDomain={legendDomain}
        legendRange={legendColors}
        yTickFormatter={yTickFormatter}
        yDomainPadding={1.05}
        yScale={{
          type: "log",
        }}
      />
    </ChartCard>
  );
};

export const MajorEventRiskComparison = () => {
  const {
    appliedWell,
    filteredWellNamesByHierarchyUnfiltered,
    assetSelectionQueryResult,
  } = useGlobalFiltersContext();

  const [hierarchyId, wellName] = appliedWell;

  const [isBaseline, setIsBaseline] = useState(true);

  const onValueChange = useCallback((value: string) => {
    if (value === "risk-comparison-or") {
      setIsBaseline(true);
    } else {
      setIsBaseline(false);
    }
  }, []);

  const majorEventRiskComparisonResult = useQuery(
    RiskManagementMajorEventComparisonChartDocument,
    {
      skip: !wellName.length,
      variables: {
        hierarchyId,
        wellName,
        isBaseline,
      },
    },
  );

  const { height, yTickFormatter } = useBaseBarChartProps();

  const { series, referenceLines, legendDomain, theme, legendColors } =
    useMemo<RiskComparisonChartConfig>(() => {
      return buildRiskComparisonChartConfig(
        {
          chartData:
            majorEventRiskComparisonResult.data
              ?.riskManagementMajorEventComparisonChart,
          wellName,
          hierarchyId,
          wellNamesByHierarchy: filteredWellNamesByHierarchyUnfiltered,
          hierarchies: assetSelectionQueryResult.data?.hierarchies,
        },
        isBaseline,
        false,
        yTickFormatter,
      );
    }, [
      isBaseline,
      hierarchyId,
      majorEventRiskComparisonResult.data,
      wellName,
      filteredWellNamesByHierarchyUnfiltered,
      assetSelectionQueryResult.data?.hierarchies,
      yTickFormatter,
    ]);

  // this chart doesn't read yAxisLabel from a route handle; isBaseline maps to OR|IR
  return (
    <ChartCard
      heading={<RiskComparisonCardHeading onValueChange={onValueChange} />}
      loading={majorEventRiskComparisonResult.loading}
    >
      <XYScatterPlot
        height={height}
        series={series}
        xAxisLabel="Nth-Ranked Well"
        yAxisLabel={`${isBaseline ? "Operational Risk" : "Intervention Risk"}`}
        theme={theme}
        referenceLines={referenceLines}
        legendDomain={legendDomain}
        legendRange={legendColors}
        yTickFormatter={yTickFormatter}
        yDomainPadding={1.05}
        yScale={{
          type: "log",
        }}
      />
    </ChartCard>
  );
};

export default MajorEventRiskComparison;
